/* ──────────────────────────────────────────────────────────────────────────
 * Round-number Y-axis ticks for recharts charts.
 *
 * Recharts' default YAxis (no `domain`, no `ticks`) pads the data range
 * and picks tick values that look mathematically nice but read as
 * arbitrary to humans — $105k, $137k, etc. — because the algorithm starts
 * from a padded floor rather than a round number.
 *
 * `niceYTicks(min, max)` returns a tick array starting and ending exactly
 * on round numbers from the 1-2-5 step series, scaled to the data range.
 * Use it on both the `ticks` and `domain` props of `<YAxis>` so the axis
 * doesn't add its own padding back.
 *
 *   const yTicks = niceYTicks(dataMin, dataMax);
 *   <YAxis ticks={yTicks} domain={[yTicks[0], yTicks[yTicks.length - 1]]} ... />
 *
 * This belongs in a shared helper because every finance chart in the
 * codebase wants the same behavior. A future slice may swap recharts for
 * TradingView Lightweight Charts — that library has built-in nice-tick
 * behavior, so this helper becomes obsolete at that point.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Compute round-number Y-axis ticks spanning [min, max].
 *
 * Picks a step from the 1-2-5 series scaled to a power of 10 that produces
 * roughly `targetCount` ticks across the range. Floors `min` and ceilings
 * `max` to the step so the resulting range starts and ends on round numbers.
 *
 * Returns at least two ticks (the floor and the next step) for any finite
 * input; if `max < min`, the inputs are swapped.
 */
export function niceYTicks(min: number, max: number, targetCount = 6): number[] {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);

  // Degenerate range — return a single-step tick array so the caller still
  // gets two valid ticks for `domain`.
  if (hi - lo < 1e-9) {
    const fallbackStep = lo === 0 ? 1 : Math.pow(10, Math.floor(Math.log10(Math.abs(lo))));
    return [lo, lo + fallbackStep];
  }

  const range = hi - lo;
  const rawStep = range / Math.max(1, targetCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;

  // 1-2-5 series: each value reads as "round" at any magnitude. Tightening
  // the thresholds (1.5, 3, 7) biases toward the smaller step when the raw
  // step sits between two nice values, which usually feels right on charts.
  let niceStep: number;
  if (normalized <= 1.5) niceStep = 1;
  else if (normalized <= 3) niceStep = 2;
  else if (normalized <= 7) niceStep = 5;
  else niceStep = 10;

  const step = niceStep * magnitude;
  const niceMin = Math.floor(lo / step) * step;
  const niceMax = Math.ceil(hi / step) * step;

  const ticks: number[] = [];
  // Half-step epsilon on the upper bound guards against float-add drift
  // dropping the last tick when niceMax sits exactly on a step boundary.
  for (let t = niceMin; t <= niceMax + step / 2; t += step) {
    ticks.push(Number(t.toFixed(10)));
  }
  return ticks;
}
